import {Grid, Typography} from '@mui/material';
import html2canvas from 'html2canvas';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReactComponent as ExportIcon} from '../../../../../src/assests/svg/export_icon.svg';
import {getProcessingSummaryByDataSetID} from '../../../../api/ReportingManagement';
import {
  getProcessingSummaryAlerts,
  updateAlertStatusAsViewed,
} from '../../../../api/UserManagementService';
import {setAlerts} from '../../../../redux/slices/alertSlice';
import {RootState} from '../../../../redux/store';
import {
  generateNOpenPDF,
  getUTCDateTimeForDisplay,
  numberFormatter,
} from '../../../../utils/utils';
import AurAlertCarousel from '../../../common/aur-alert/AurAlertCarousel';
import {AurDropdownButton} from '../../../common/aur-button/AurDropdownButton';
import CardItem from './CardItem';
import ProcessingHistoryChart from './ProcessingHistoryChart/ProcessingHistoryChart';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {ReportScreenTabTypes} from '../../../pages/reports/ProjectOverview';
import {
  failedToLoadStatusCodes,
  ONLY_DATE_SHOW_FORMAT,
} from '../../../../constants/constants';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
import isBoolean from 'lodash/isBoolean';

export interface ProcessingSummaryReportType {
  datasetSummary: string;
  completedProcessing: string;
  description: string;
  custodian: string;
  docsAvailableForIngestion: number;
  aggNativeVolumeOfDocs: number;
  actionableExceptions: number;
  nonActionableExceptions: number;
  processingSummary: ProcessingSummaryItemType[];
}

interface ProcessingSummaryItemType {
  type: string;
  fileCount: number;
  totalSize: number;
}

export const ProcessingSummaryTypeMapToCaption: {[key: string]: string} = {
  SubmittedToProcessing: 'submittedToProcessing',
  Expanded: 'expanded',
  PostDuplication: 'postDuplication',
  AvailableForReview: 'availableForReview',
};

interface CardGridDataType {
  number: number;
  label: string;
  showInfo: boolean;
  tooltip?: string;
}

export const EXPORT_DROPDOWN_ID = 'export-report-dropdown';
const REPORT_SOURCE_ROW_ID = 'report-source-row';

interface Props {
  skipTabTracking?: boolean;
  openSpecificTab?: (type: ReportScreenTabTypes) => void;
}

const abortControllers: Map<string, AbortController> = new Map();

const ProcessingSummaryTab: React.FC<Props> = ({
  openSpecificTab,
  skipTabTracking = false,
}) => {
  const parentElementRef = useRef<HTMLDivElement | null>(null);
  const [processingSummaryData, setProcessingSummaryData] =
    useState<ProcessingSummaryReportType | null>(null);
  const [toggleAlertState, setToggleAlertState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertLoading, setIsAlertLoading] = useState(false);
  const [isFailedToLoadAlert, setIsFailedToLoadAlert] =
    useState<boolean>(false);
  const [isFailedToLoadSummary, setIsFailedToLoadSummary] =
    useState<boolean>(false);
  const {
    workspaceDataSetName,
    clientId,
    projectId,
    workspaceId,
    workspaceDataSetCustodianId,
  } = useSelector((state: RootState) => state.reports);
  const alertData = useSelector((state: RootState) => state.alerts);
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const {canViewProcessingSummaryReports, canViewProcessingExceptionReports} =
    usePermissionsByPage(PAGE_NAME.PROCESSING_SUMMARY_EXCEPTIONS_TAB);

  const isBoolPerm = useMemo(
    () =>
      isBoolean(canViewProcessingSummaryReports) &&
      isBoolean(canViewProcessingExceptionReports),
    [canViewProcessingSummaryReports, canViewProcessingExceptionReports]
  );

  const apiResponseTime = useRef('');
  useEffect(() => {
    isBoolPerm &&
      (canViewProcessingSummaryReports ||
        (!canViewProcessingSummaryReports &&
          !canViewProcessingExceptionReports)) &&
      fetchProcessingSummary();
  }, [
    workspaceDataSetName,
    clientId,
    projectId,
    workspaceId,
    workspaceDataSetCustodianId,
    canViewProcessingSummaryReports,
    canViewProcessingExceptionReports,
    isBoolPerm,
  ]);

  useEffect(() => {
    if (projectId && clientId && workspaceId)
      isBoolPerm &&
        (canViewProcessingSummaryReports ||
          (!canViewProcessingSummaryReports &&
            !canViewProcessingExceptionReports)) &&
        fetchAlertData();
  }, [
    clientId,
    projectId,
    workspaceId,
    toggleAlertState,
    canViewProcessingSummaryReports,
    canViewProcessingExceptionReports,
    isBoolPerm,
  ]);

  useEffect(() => {
    // Clean up
    return () => {
      abortControllers.forEach((controller) => {
        isBoolPerm &&
          (canViewProcessingSummaryReports ||
            canViewProcessingExceptionReports) &&
          controller.abort();
      });
      abortControllers.clear();
    };
  }, [
    canViewProcessingSummaryReports,
    canViewProcessingExceptionReports,
    isBoolPerm,
  ]);

  const fetchProcessingSummary = async () => {
    const url = '/processing-summary';
    if (workspaceDataSetName && clientId && projectId && workspaceId) {
      setIsLoading(true);
      let paramString = `?clientId=${clientId}&projectId=${projectId}&workspaceId=${workspaceId}`;
      if (workspaceDataSetCustodianId) {
        paramString =
          paramString + `&custodianId=${workspaceDataSetCustodianId}`;
      }
      try {
        if (abortControllers.has(url)) {
          abortControllers.get(url)?.abort();
        }

        const controller = new AbortController();
        abortControllers.set(url, controller);
        setIsFailedToLoadSummary(false);
        const result = await getProcessingSummaryByDataSetID(
          workspaceDataSetName ?? '',
          paramString,
          controller.signal
        );
        if (result.data.data && result.data.data.length > 0)
          setProcessingSummaryData(result.data.data[0]);

        setIsLoading(false);
        abortControllers.delete(url);
      } catch (error: any) {
        if (error.name === 'CanceledError') {
          return;
        }
        setIsLoading(false);
        if (failedToLoadStatusCodes.includes(error.status)) {
          setIsFailedToLoadSummary(true);
        }
        setProcessingSummaryData(null);
        abortControllers.delete(url);
      }
    }
  };

  const fetchAlertData = async () => {
    const url = '/alerts/processing-summary';
    if (clientId && projectId && workspaceId) {
      setIsAlertLoading(true);
      try {
        if (abortControllers.has(url)) {
          abortControllers.get(url)?.abort();
        }

        const controller = new AbortController();
        abortControllers.set(url, controller);
        setIsFailedToLoadAlert(false);
        const responseFetchAlert = await getProcessingSummaryAlerts(
          clientId,
          projectId,
          workspaceId,
          controller.signal
        );
        abortControllers.delete(url);
        dispatch(setAlerts(responseFetchAlert?.data?.data));
        setIsAlertLoading(false);
        apiResponseTime.current = responseFetchAlert.data.responseTime;
      } catch (error: any) {
        if (error.name === 'CanceledError') {
          return;
        }
        if (failedToLoadStatusCodes.includes(error.status))
          setIsFailedToLoadAlert(true);
        setIsAlertLoading(false);
        abortControllers.delete(url);
        apiResponseTime.current = error.responseTime;
      } finally {
        !skipTabTracking &&
          trackGAEvent({
            eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
            subPage: {
              subpage_name: t('gaTracking.reports.processingSummary'),
              subpage_type: GA_SUBPAGE_TYPE.TAB,
            },
            user: GA_DATA_MODE_AUTO,
          });
      }
    }
  };

  const markAlertAsViewed = (userAlertId: number) => {
    updateAlertStatusAsViewed(userAlertId);
    setToggleAlertState((prevState) => !prevState);
  };

  const handleIntakeTabClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    if (openSpecificTab)
      openSpecificTab(ReportScreenTabTypes.INTAKE_AUDIT_REPORT);
  };

  const cardGridData = useMemo(() => {
    if (!processingSummaryData) return [];

    // Map of keys to corresponding labels and showInfo values
    const keyMapping: {
      [key: string]: CardGridDataType;
    } = {
      docsAvailableForIngestion: {
        label: 'reports.processingSummary.docsAvailableForIngestion',
        showInfo: false,
        number: 0,
      },
      aggNativeVolumeOfDocs: {
        label: 'reports.processingSummary.aggNativeVolumeOfDocs',
        showInfo: false,
        number: 0,
      },
      actionableExceptions: {
        label: 'reports.processingSummary.actionableExceptions',
        showInfo: true,
        tooltip: t('reports.processingSummary.actionableExceptionsToolTip'),
        number: 0,
      },
      nonActionableExceptions: {
        label: 'reports.processingSummary.nonActionableExceptions',
        showInfo: true,
        tooltip: t('reports.processingSummary.nonActionableExceptionsToolTip'),
        number: 0,
      },
    };

    // Map through the keys and build the data array
    return Object.keys(keyMapping)
      .reduce((data: CardGridDataType[], key) => {
        const keyAsString = key as keyof ProcessingSummaryReportType;
        if (processingSummaryData[keyAsString] !== undefined) {
          data.push({
            number: processingSummaryData[keyAsString] as number,
            label: keyMapping[keyAsString].label,
            showInfo: keyMapping[keyAsString].showInfo,
            tooltip: keyMapping[keyAsString].tooltip,
          });
        }
        return data;
      }, [])
      .map((obj) => ({...obj, number: numberFormatter(obj.number)}));
  }, [processingSummaryData]);

  const hideShowComponentForReport = (visible: boolean) => {
    if (parentElementRef.current) {
      const exportButton = parentElementRef.current.querySelector(
        `#${EXPORT_DROPDOWN_ID}`
      ) as HTMLDivElement;

      if (exportButton)
        exportButton.style.visibility = visible ? 'visible' : 'hidden';

      const sourceRowElement = parentElementRef.current.querySelector(
        `#${REPORT_SOURCE_ROW_ID}`
      ) as HTMLDivElement;

      if (sourceRowElement)
        sourceRowElement.style.visibility = visible ? 'visible' : 'hidden';
    }
  };

  const downloadNExportReport = useCallback(() => {
    if (parentElementRef.current) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.EXPORT,
        click: {
          name: t('gaTracking.reports.exportOption.pdf'),
          type: GA_CLICK_TYPES.DROPDOWN,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.reports.exportReport'),
        },
        file_extension: GA_FILE_EXTENTIONS.PDF,
        ...(!skipTabTracking && {
          subPage: {
            subpage_name: t('gaTracking.reports.processingSummary'),
            subpage_type: GA_SUBPAGE_TYPE.TAB,
          },
        }),
      });
      hideShowComponentForReport(false);

      html2canvas(parentElementRef.current, {scale: 1})
        .then((canvas) => {
          const imgData = canvas.toDataURL('image/jpeg');
          generateNOpenPDF(imgData, canvas);
        })
        .finally(() => {
          if (parentElementRef.current) {
            hideShowComponentForReport(true);
          }
        });
    }
  }, []);
  const renderAlert = () => {
    if (isLoading) return;
    if (isAlertLoading)
      return (
        <div className="alert-loading">
          <AurLoadingIndicator />
        </div>
      );

    if (isFailedToLoadAlert)
      return (
        <div className="alert-error">
          <AurDataGridErrorOverlay
            onRefresh={fetchAlertData}
            enableParentWrapper={false}
          />
        </div>
      );

    return alertData?.alerts?.length > 0 ? (
      <div className="processing-summary-alert">
        <AurAlertCarousel
          alertsData={
            alertData?.alerts.map((alert) => ({
              ...alert,
              deleteButtonHandler: () => markAlertAsViewed(alert.userAlertId),
            })) as []
          }
        />
      </div>
    ) : null;
  };

  const renderData = () => {
    if (isLoading)
      return (
        <div className="data-loading">
          <AurLoadingIndicator />
        </div>
      );

    if (isFailedToLoadSummary)
      return (
        <div className="data-error">
          <AurDataGridErrorOverlay
            onRefresh={fetchProcessingSummary}
            enableParentWrapper={false}
          />
        </div>
      );

    return processingSummaryData ? (
      <div className="summary-report-container" ref={parentElementRef}>
        <div className="processing-summary-report-description">
          <div className="processing-summary-report-description-header">
            <Typography className="typo-text headline2_size">
              {t('reports.processingSummary.dataSetSummary', {
                value: processingSummaryData.datasetSummary ?? '',
              })}
            </Typography>

            <AurDropdownButton
              dropdownRole="dropdown"
              hintText={t('processingSummary.exportOptionHint', {
                datasetSummary: processingSummaryData.datasetSummary,
              })}
              iconType="Other"
              buttonText={t('common.exportReport')}
              classNames="typo-text buttonMedium_size "
              startIcon={<ExportIcon aria-hidden="true" />}
              type="continue"
              size="Medium"
              aria-live="polite"
              iconSize="medium"
              iconClassName="client-profile-details-dropdown-icon"
              menuDirectionLeft={false}
              menuItemHeight="large"
              menuItemWidth="large"
              menuItemTextSize="large"
              menuClassNames="export-button-drop-down"
              elementID={EXPORT_DROPDOWN_ID}
              menuItems={[
                {
                  label: t('common.exportOption.pdf'),
                  onClick: downloadNExportReport,
                },
                // Due to requirements commenting this code as of now
                // {
                //   label: t(
                //     'reports.processingSummary.menuItemsLabel.sendPDFViaEmail'
                //   ),
                //   onClick: () => {},
                // },
              ]}
            />
          </div>
          <div className="processing-summary-report-description-content">
            <div className="row">
              <span className="typo-text subhead3_size">
                {t('reports.processingSummary.completedProcessing')}
              </span>
              <AurLightTooltip title={t('common.yearMonthDate')}>
                <span className="typo-text body1_size text">
                  {getUTCDateTimeForDisplay(
                    processingSummaryData.completedProcessing,
                    ONLY_DATE_SHOW_FORMAT
                  )}
                </span>
              </AurLightTooltip>
            </div>
            <div className="row">
              <span className="typo-text subhead3_size">
                {t('reports.processingSummary.custodian')}
              </span>
              <span className="typo-text body1_size text">
                {processingSummaryData.custodian ?? ''}
              </span>
            </div>
            <div className="row" id={REPORT_SOURCE_ROW_ID}>
              <span className="typo-text subhead3_size">
                {t('reports.processingSummary.source')}
              </span>
              <a
                title={t('reports.intakeAuditReportTitle')}
                role="link"
                aria-label={t('reports.intakeAuditReportAriaLabel')}
                href="#"
                className="typo-text body1_size link"
                onClick={handleIntakeTabClick}
              >
                {t('reports.processingInProgress.intakeAuditReport')}
              </a>
            </div>
          </div>
          <Grid
            className="processing-summary-report-description-grid"
            container
            spacing={2}
            sx={{padding: '48px 20px 20px 0'}}
          >
            {cardGridData.map((item, index) => (
              <Grid item key={index} xs={12} sm={6} md={3}>
                <CardItem
                  number={item.number.toString()}
                  label={t(item.label)}
                  showInfo={item.showInfo}
                  tooltipTitle={item.tooltip}
                />
              </Grid>
            ))}
          </Grid>
        </div>
        <ProcessingHistoryChart data={processingSummaryData} />
      </div>
    ) : null;
  };
  return (
    <div className="processing-summary-content">
      <div className="processing-summary-description">
        {t('reports.processingSummary.processingDataDescription')}
      </div>
      {renderAlert()}
      {renderData()}
    </div>
  );
};

export default ProcessingSummaryTab;
